import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Stack,
  Alert,
  CircularProgress,
  LinearProgress,
  Typography,
  Box
} from "@mui/material";
import { useApi } from "../utils/api";
import { ENDPOINTS } from "../config/endpoints";

// Backend ile uyumlu istihdam türleri
const ISTIHDAM_TURLERI = [
  {
    value: "kadrolu_ogretmen",
    label: "Kadrolu Öğretmen",
  },
  {
    value: "ders_saati_ucretli_ogretmen",
    label: "Ders Saati Ücretli Öğretmen",
  },
  {
    value: "tam_zamanli_yardimci_personel",
    label: "Tam Zamanlı Personel",
  },
  {
    value: "kismi_zamanli_yardimci_personel",
    label: "Kısmi Zamanlı Personel",
  },
];

const CINSIYETLER = [
  { value: "erkek", label: "Erkek" },
  { value: "kadin", label: "Kadın" },
];

const UCRET_TURLERI = [
  { value: "net", label: "Net Ücret" },
  { value: "brut", label: "Brüt Ücret" },
];

const STEPS = [
  "Genel Bilgiler",
  "İletişim Bilgileri",
  "İstihdam Bilgileri",
  "Aile ve Durum Bilgileri",
];

const calcAge = (birthDate) => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};


const AddEmployeeModal = ({
  open,
  onClose,
  employee,
  onEmployeeAdded,
  onEmployeeUpdated,
  fetchEmployees,
  userData,
}) => {
  const http = useApi();
  const isEdit = !!employee;
  const isKurum = userData?.type === "kurum";

  const [form, setForm] = useState({
    kurum_id: "",
    departman_id: "",
    pozisyon_id: "",

    cinsiyet: "",
    ad: "",
    soyad: "",
    tc: "",
    anne_adi: "",
    baba_adi: "",
    telefon: "",
    mail: "",
    adres: "",

    dogum_tarihi: "",
    ise_giris_tarihi: "",

    istihdam_turu: "",

    maas: "",

    // STEP 3
    hicbiri: false,

    evli_mi: false,
    cocuk_sayisi: 0,
    cocuk_yardimi_aliyor_mu: false,
    // cocuklar: [], // [{ dogum_tarihi: "" }]
    cocuklar: [
      {
        dogum_tarihi: "",
        durum: "",        // "okuyor" | "okumuyor" | "calisiyor"
        egitim_seviyesi: "" // "ilkokul" | "lise" | "universite"
      }
    ],


    es_calisiyor_mu: false,
    es_calisma_turu: "", // "kamu" | "ozel"

    emekli_mi: false,
    kamuda_aktif_gorevli_mi: false,
    engelli_mi: false,

    // ÜCRET
    ucret_turu: "",
    saatlik_ucret: "",
  });


  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [kurums, setKurums] = useState([]);
  const [deps, setDeps] = useState([]);
  const [poz, setPoz] = useState([]);
  const [filteredPoz, setFilteredPoz] = useState([]);
  const [ucretAyar, setUcretAyar] = useState(null);
  const [ucretLoading, setUcretLoading] = useState(false);


  const [step, setStep] = useState(0); // 0..3

  // modal açıldığında verileri çek + adımı sıfırla
  useEffect(() => {
    if (open) {
      setStep(0);
      fetchKurums();
      fetchDeps();
      fetchPoz();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (employee && open) {
      setForm({
        kurum_id: employee.kurum?.id || employee.kurum_id || "",
        departman_id: employee.departman?.id || employee.departman_id || "",
        pozisyon_id: employee.pozisyon?.id || employee.pozisyon_id || "",

        cinsiyet: employee.cinsiyet || "",
        ad: employee.ad || "",
        soyad: employee.soyad || "",
        tc: employee.tc || "",
        anne_adi: employee.anne_adi || "",
        baba_adi: employee.baba_adi || "",
        telefon: employee.telefon || "",
        mail: employee.mail || "",
        adres: employee.adres || "",

        dogum_tarihi: employee.dogum_tarihi || "",
        ise_giris_tarihi: employee.ise_giris_tarihi || "",

        istihdam_turu: employee.istihdam_turu || "",

        maas: employee.maas ?? "",
        saatlik_ucret: employee.saatlik_ucret ?? "",
        ucret_turu: employee.ucret_turu || "",

        // Aile & Durum — bunlar edit'te eksikti!
        hicbiri: !!employee.hicbiri,
        evli_mi: !!employee.evli_mi,
        cocuk_sayisi: employee.cocuk_sayisi ?? 0,
        cocuk_yardimi_aliyor_mu: !!employee.cocuk_yardimi_aliyor_mu,
        cocuklar: Array.isArray(employee.cocuklar) && employee.cocuklar.length > 0
          ? employee.cocuklar.map((c) => ({
            dogum_tarihi: c.dogum_tarihi || "",
            durum: c.durum || "",
            egitim_seviyesi: c.egitim_seviyesi || "",
          }))
          : [],

        es_calisiyor_mu: !!employee.es_calisiyor_mu,
        es_calisma_turu: employee.es_calisma_turu || "",

        emekli_mi: !!employee.emekli_mi,
        kamuda_aktif_gorevli_mi: !!employee.kamuda_aktif_gorevli_mi,
        engelli_mi: !!employee.engelli_mi,
      });
    } else if (open && !employee) {
      setForm({
        kurum_id: isKurum ? userData.id : "",
        departman_id: "",
        pozisyon_id: "",
        cinsiyet: "",
        ad: "",
        soyad: "",
        tc: "",
        anne_adi: "",
        baba_adi: "",
        telefon: "",
        mail: "",
        adres: "",
        dogum_tarihi: "",
        ise_giris_tarihi: "",
        istihdam_turu: "",
        maas: "",
        saatlik_ucret: "",
        ucret_turu: "",
        hicbiri: false,
        evli_mi: false,
        cocuk_sayisi: 0,
        cocuk_yardimi_aliyor_mu: false,
        cocuklar: [],
        es_calisiyor_mu: false,
        es_calisma_turu: "",
        emekli_mi: false,
        kamuda_aktif_gorevli_mi: false,
        engelli_mi: false,
      });
    }
  }, [employee, open]);

  // Departmana göre pozisyon filtrele
  useEffect(() => {
    if (form.departman_id) {
      setFilteredPoz(
        poz.filter(
          (p) => String(p.departman_id) === String(form.departman_id)
        )
      );
    } else {
      setFilteredPoz([]);
    }
  }, [form.departman_id, poz]);

  const fetchKurums = async () => {
    try {
      const data = await http.get(ENDPOINTS.KURUM.LIST, {
        skip: 0,
        limit: 100,
      });
      setKurums(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Kurumlar alınamadı:", e);
    }
  };

  const fetchUcretAyarlari = async () => {
    try {
      setUcretLoading(true);
      const data = await http.get("/ucret-ayarlari");
      setUcretAyar(data);
    } catch (e) {
      console.error("Ücret ayarları alınamadı", e);
    } finally {
      setUcretLoading(false);
    }
  };


  const fetchDeps = async () => {
    try {
      const data = await http.get(ENDPOINTS.DEPARTMAN.LIST, {
        skip: 0,
        limit: 50,
      });
      setDeps(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Departmanlar alınamadı:", e);
    }
  };

  const fetchPoz = async () => {
    try {
      const data = await http.get(ENDPOINTS.POZISYON.LIST, {
        skip: 0,
        limit: 100,
      });
      setPoz(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Pozisyonlar alınamadı:", e);
    }
  };

  // Her adım için basit doğrulama
  const validateStep = (currentStep) => {
    // error'ı sıfırla, sonra doldur
    setError("");

    if (currentStep === 0) {
      // Genel Bilgiler
      if (currentStep === 0) {
        if (!form.ad || !form.soyad) {
          setError("Lütfen ad ve soyad giriniz.");
          return false;
        }
        if (!form.tc || form.tc.length !== 11) {
          setError("Lütfen 11 haneli TC giriniz.");
          return false;
        }
      }
    }

    return true;
  };

  const handleNext = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setError("");
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleSubmit = async () => {
    if (!validateStep(step)) return;

    setError("");
    setLoading(true);

    const payload = {
      ad: form.ad,
      soyad: form.soyad,
      tc: form.tc,
      kurum_id: Number(form.kurum_id),

      cinsiyet: form.cinsiyet || null,
      anne_adi: form.anne_adi || null,
      baba_adi: form.baba_adi || null,
      telefon: form.telefon || null,
      mail: form.mail || null,
      adres: form.adres || null,

      dogum_tarihi: form.dogum_tarihi || null,
      ise_giris_tarihi: form.ise_giris_tarihi || null,

      departman_id: form.departman_id ? Number(form.departman_id) : null,
      pozisyon_id: form.pozisyon_id ? Number(form.pozisyon_id) : null,

      istihdam_turu: form.istihdam_turu || null,
      ucret_turu: form.ucret_turu || null,
      saatlik_ucret: form.saatlik_ucret !== "" ? form.saatlik_ucret : null,
      maas: form.maas !== "" ? form.maas : null,

      // Aile & Durum
      hicbiri: form.hicbiri,
      evli_mi: form.evli_mi,
      cocuk_sayisi: form.cocuk_sayisi ?? 0,
      cocuk_yardimi_aliyor_mu: form.cocuk_yardimi_aliyor_mu,
      cocuklar: form.cocuklar ?? [],
      es_calisiyor_mu: form.es_calisiyor_mu,
      es_calisma_turu: form.es_calisiyor_mu ? (form.es_calisma_turu || null) : null,
      emekli_mi: form.emekli_mi,
      kamuda_aktif_gorevli_mi: form.kamuda_aktif_gorevli_mi,
      engelli_mi: form.engelli_mi,
    };

    try {
      let data;
      if (isEdit) {
        data = await http.put(ENDPOINTS.PERSONEL.DETAIL(employee.id), payload);
        onEmployeeUpdated(data);
      } else {
        data = await http.post(ENDPOINTS.PERSONEL.LIST, payload);
        onEmployeeAdded(data);
      }
      onClose();
      fetchEmployees();
    } catch (err) {
      setError(err.message || "İşlem başarısız");
    } finally {
      setLoading(false);
    }
  };

  const progressValue = ((step + 1) / STEPS.length) * 100;

  const handleHicbiri = (checked) => {
    if (checked) {
      setForm((s) => ({
        ...s,                          // ← diğer alanları koru
        hicbiri: true,
        evli_mi: false,
        cocuk_sayisi: 0,
        cocuk_yardimi_aliyor_mu: false,
        cocuklar: [],
        es_calisiyor_mu: false,
        es_calisma_turu: "",
        emekli_mi: false,
        kamuda_aktif_gorevli_mi: false,
        engelli_mi: false,
      }));
    } else {
      setForm((s) => ({ ...s, hicbiri: false }));
    }
  };

  const handleCocukSayisi = (value) => {
    const sayi = Number(value);
    const prev = form.cocuklar || [];

    setForm((s) => ({
      ...s,
      cocuk_sayisi: sayi,
      cocuklar: Array.from({ length: sayi }, (_, i) => ({
        dogum_tarihi: prev[i]?.dogum_tarihi || "",
        durum: prev[i]?.durum || "",
        egitim_seviyesi: prev[i]?.egitim_seviyesi || "",
      })),
    }));
  };

  useEffect(() => {
    if (step === 2 && !ucretAyar) {
      fetchUcretAyarlari();
    }
  }, [step]);

  useEffect(() => {
    if (!ucretAyar) return;
    if (step !== 2) return;

    if (form.ucret_turu === "net") {
      setForm((s) => ({
        ...s,
        saatlik_ucret: ucretAyar.net_saatlik_asgari ?? "",
        maas: ucretAyar.net_aylik_asgari ?? "",
      }));
    }

    if (form.ucret_turu === "brut") {
      setForm((s) => ({
        ...s,
        saatlik_ucret: ucretAyar.brut_saatlik_asgari ?? "",
        maas: ucretAyar.brut_aylik_asgari ?? "",
      }));
    }
  }, [form.ucret_turu, ucretAyar, step]);





  return (
    // <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
    >

      <DialogTitle>
        {isEdit ? "Personel Güncelle" : "Personel Ekle"}
      </DialogTitle>

      {/* İlerleme Çubuğu + Adım Başlığı */}
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5 }}
          >
            Adım {step + 1} / {STEPS.length} — {STEPS[step]}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{ height: 6, borderRadius: 999 }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* ADIM 1: GENEL BİLGİLER */}
        {step === 0 && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Kurum*</InputLabel>
              <Select
                value={form.kurum_id}
                label="Kurum*"
                disabled={isKurum}          // ← ekle
                onChange={(e) =>
                  setForm((s) => ({ ...s, kurum_id: e.target.value }))
                }
              >
                {kurums?.map((k) => (
                  <MenuItem key={k.id} value={k.id}>
                    {k.kurum_adi || k.ad || `Kurum #${k.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1}>
              <TextField
                label="Ad*"
                value={form.ad}
                onChange={(e) =>
                  setForm((s) => ({ ...s, ad: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Soyad*"
                value={form.soyad}
                onChange={(e) =>
                  setForm((s) => ({ ...s, soyad: e.target.value }))
                }
                fullWidth
              />
            </Stack>

            <Stack direction="row" spacing={1}>
              <FormControl fullWidth>
                <InputLabel>Cinsiyet*</InputLabel>
                <Select
                  value={form.cinsiyet}
                  label="Cinsiyet*"
                  onChange={(e) =>
                    setForm((s) => ({ ...s, cinsiyet: e.target.value }))
                  }
                >
                  {CINSIYETLER?.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="TC*"
                value={form.tc}
                onChange={(e) =>
                  setForm((s) => ({ ...s, tc: e.target.value }))
                }
                inputProps={{ maxLength: 11 }}
                fullWidth
              />
            </Stack>

            <Stack direction="row" spacing={1}>
              <TextField
                label="Anne Adı"
                value={form.anne_adi}
                onChange={(e) =>
                  setForm((s) => ({ ...s, anne_adi: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Baba Adı"
                value={form.baba_adi}
                onChange={(e) =>
                  setForm((s) => ({ ...s, baba_adi: e.target.value }))
                }
                fullWidth
              />
            </Stack>

            <TextField
              type="date"
              label="Doğum Tarihi"
              value={form.dogum_tarihi}
              onChange={(e) =>
                setForm((s) => ({
                  ...s,
                  dogum_tarihi: e.target.value,
                }))
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        )}

        {/* ADIM 2: İLETİŞİM BİLGİLERİ */}
        {step === 1 && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1}>
              <TextField
                label="Mail"
                value={form.mail}
                onChange={(e) =>
                  setForm((s) => ({ ...s, mail: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Telefon"
                value={form.telefon}
                onChange={(e) =>
                  setForm((s) => ({ ...s, telefon: e.target.value }))
                }
                fullWidth
              />
            </Stack>

            <TextField
              label="Adres"
              value={form.adres}
              onChange={(e) =>
                setForm((s) => ({ ...s, adres: e.target.value }))
              }
              multiline
              rows={3}
            />
          </Stack>
        )}

        {/* ADIM 3: İSTİHDAM BİLGİLERİ */}
        {/* ADIM 3: İSTİHDAM BİLGİLERİ */}
        {step === 2 && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={1}>
              <FormControl fullWidth>
                <InputLabel>Departman*</InputLabel>
                <Select
                  value={form.departman_id}
                  label="Departman*"
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      departman_id: e.target.value,
                      pozisyon_id: "",
                    }))
                  }
                >
                  {deps?.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.ad}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Pozisyon*</InputLabel>
                <Select
                  value={form.pozisyon_id}
                  label="Pozisyon*"
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      pozisyon_id: e.target.value,
                    }))
                  }
                >
                  {filteredPoz?.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.ad}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={1}>
              <TextField
                type="date"
                label="İşe Giriş Tarihi*"
                value={form.ise_giris_tarihi}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    ise_giris_tarihi: e.target.value,
                  }))
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <FormControl fullWidth>
                <InputLabel>İstihdam Türü*</InputLabel>
                <Select
                  value={form.istihdam_turu}
                  label="İstihdam Türü*"
                  onChange={(e) =>
                    setForm((s) => ({
                      ...s,
                      istihdam_turu: e.target.value,
                    }))
                  }
                >
                  {ISTIHDAM_TURLERI?.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Ücret Bilgileri */}
            <Stack direction="row" spacing={1}>
              <FormControl fullWidth>
                <InputLabel>Ücret Türü*</InputLabel>
                <Select
                  value={form.ucret_turu}
                  label="Ücret Türü*"
                  onChange={(e) =>
                    setForm((s) => ({ ...s, ucret_turu: e.target.value }))
                  }
                >
                  {UCRET_TURLERI?.map((u) => (
                    <MenuItem key={u.value} value={u.value}>
                      {u.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Ücret alanlarını istihdam türüne göre göster */}
              {(() => {
                const t = form.istihdam_turu;
                const showSaatlikUcret = ["ders_saati_ucretli_ogretmen", "kadrolu_ogretmen", "kismi_zamanli_yardimci_personel"].includes(t);
                const showMaas = ["kadrolu_ogretmen", "kismi_zamanli_yardimci_personel", "tam_zamanli_yardimci_personel"].includes(t);

                return (
                  <>
                    {showSaatlikUcret && (
                      <TextField
                        type="number"
                        label="Saatlik Ücret*"
                        value={form.saatlik_ucret}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, saatlik_ucret: e.target.value }))
                        }
                        fullWidth
                      />
                    )}
                    {showMaas && (
                      <TextField
                        type="number"
                        label="Maaş*"
                        value={form.maas || ""}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, maas: e.target.value }))
                        }
                        fullWidth
                      />
                    )}
                  </>
                );
              })()}
            </Stack>
          </Stack>
        )}


        {/* ADIM 4: AİLE VE DURUM BİLGİLERİ */}
        {step === 3 && (
          <Stack spacing={2} sx={{ mt: 1 }}>

            {/* HİÇBİRİ */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.hicbiri}
                  onChange={(e) => handleHicbiri(e.target.checked)}
                />
              }
              label="Hiçbiri"
            />

            {!form.hicbiri && (
              <>
                {/* EVLİ */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.evli_mi}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          hicbiri: false,
                          evli_mi: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Evli mi?"
                />

                {/* EVLİYSE */}
                {form.evli_mi && (
                  <Stack spacing={2}>
                    <TextField
                      type="number"
                      label="Çocuk Sayısı"
                      value={form.cocuk_sayisi}
                      onChange={(e) => handleCocukSayisi(e.target.value)}
                      sx={{ width: 180 }}
                    />

                    {form.cocuk_sayisi > 0 && (
                      <>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={form.cocuk_yardimi_aliyor_mu}
                              onChange={(e) =>
                                setForm((s) => ({
                                  ...s,
                                  cocuk_yardimi_aliyor_mu: e.target.checked,
                                }))
                              }
                            />
                          }
                          label="Çocuk yardımı alıyor mu?"
                        />

                        {form.cocuklar?.map((cocuk, i) => {
                          const yas = calcAge(cocuk.dogum_tarihi);
                          const uygunMu = yas !== null && yas >= 6 && yas <= 25;

                          return (
                            <Box
                              key={i}
                              sx={{
                                border: "1px solid #ddd",
                                borderRadius: 2,
                                p: 2,
                                mt: 1,
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                {i + 1}. Çocuk {yas !== null && `(Yaş: ${yas})`}
                              </Typography>

                              {/* Doğum Tarihi */}
                              <TextField
                                type="date"
                                label="Doğum Tarihi"
                                InputLabelProps={{ shrink: true }}
                                value={cocuk.dogum_tarihi}
                                onChange={(e) => {
                                  const arr = [...form.cocuklar];
                                  arr[i] = {
                                    ...arr[i],
                                    dogum_tarihi: e.target.value,
                                    durum: "",
                                    egitim_seviyesi: "",
                                  };
                                  setForm((s) => ({ ...s, cocuklar: arr }));
                                }}
                                fullWidth
                              />

                              {/* 6–25 yaş arasıysa */}
                              {uygunMu && (
                                <Stack spacing={2} sx={{ mt: 2 }}>
                                  {/* Durum */}
                                  <FormControl fullWidth>
                                    <InputLabel>Durum</InputLabel>
                                    <Select
                                      value={cocuk.durum}
                                      label="Durum"
                                      onChange={(e) => {
                                        const arr = [...form.cocuklar];
                                        arr[i].durum = e.target.value;
                                        arr[i].egitim_seviyesi = "";
                                        setForm((s) => ({ ...s, cocuklar: arr }));
                                      }}
                                    >
                                      <MenuItem value="okuyor">Okuyor</MenuItem>
                                      <MenuItem value="okumuyor">Okumuyor</MenuItem>
                                      <MenuItem value="calisiyor">Çalışıyor</MenuItem>
                                    </Select>
                                  </FormControl>

                                  {/* Okuyor seçildiyse */}
                                  {cocuk.durum === "okuyor" && (
                                    <FormControl fullWidth>
                                      <InputLabel>Eğitim Seviyesi</InputLabel>
                                      <Select
                                        value={cocuk.egitim_seviyesi}
                                        label="Eğitim Seviyesi"
                                        onChange={(e) => {
                                          const arr = [...form.cocuklar];
                                          arr[i].egitim_seviyesi = e.target.value;
                                          setForm((s) => ({ ...s, cocuklar: arr }));
                                        }}
                                      >
                                        <MenuItem value="ilkokul">İlkokul</MenuItem>
                                        <MenuItem value="ortaokul">Ortaokul</MenuItem>
                                        <MenuItem value="lise">Lise</MenuItem>
                                        <MenuItem value="universite">Üniversite</MenuItem>
                                      </Select>
                                    </FormControl>
                                  )}
                                </Stack>
                              )}
                            </Box>
                          );
                        })}

                      </>
                    )}

                    {/* EŞ ÇALIŞIYOR MU */}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={form.es_calisiyor_mu}
                          onChange={(e) =>
                            setForm((s) => ({
                              ...s,
                              es_calisiyor_mu: e.target.checked,
                              es_calisma_turu: "",
                            }))
                          }
                        />
                      }
                      label="Eş çalışıyor mu?"
                    />

                    {form.es_calisiyor_mu && (
                      <FormControl fullWidth>
                        <InputLabel>Eş Çalışma Türü</InputLabel>
                        <Select
                          value={form.es_calisma_turu}
                          label="Eş Çalışma Türü"
                          onChange={(e) =>
                            setForm((s) => ({
                              ...s,
                              es_calisma_turu: e.target.value,
                            }))
                          }
                        >
                          <MenuItem value="kamu">Kamu</MenuItem>
                          <MenuItem value="ozel">Özel</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Stack>
                )}

                {/* DİĞERLERİ */}
                <Stack direction="row" spacing={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.emekli_mi}
                        onChange={(e) =>
                          setForm((s) => ({
                            ...s,
                            hicbiri: false,
                            emekli_mi: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Emekli mi?"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.kamuda_aktif_gorevli_mi}
                        onChange={(e) =>
                          setForm((s) => ({
                            ...s,
                            hicbiri: false,
                            kamuda_aktif_gorevli_mi: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Kamuda Aktif Görevli mi?"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.engelli_mi}
                        onChange={(e) =>
                          setForm((s) => ({
                            ...s,
                            hicbiri: false,
                            engelli_mi: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Engelli mi?"
                  />
                </Stack>
              </>
            )}
          </Stack>
        )}

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          İptal
        </Button>

        {step > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Geri
          </Button>
        )}

        {step < STEPS.length - 1 && (
          <Button variant="contained" onClick={handleNext} disabled={loading}>
            İleri
          </Button>
        )}

        {step === STEPS.length - 1 && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : isEdit ? "Güncelle" : "Kaydet"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddEmployeeModal;
